/* eslint-disable camelcase */
import React from 'react'

import { DashboardContext } from 'live/context/dashboard'
import Utils from 'live/lib/utils'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { useDashboardHeight, useDashboardTimestamp, useStaticData } from '../hooks'
import {
    checkAndMountTimestamp,
    checkIfTimestampIsZero,
    selectedRunBlock,
    selectedInterventionBlock,
    selectedSectionBlock,
    mountInterventionInfo,
    setDashboardTimespan,
    mountSectionInfo,
    mountRunInfo
} from '../utils'
import { Block, Container, InnerContainer, Item, ItemRow, WrapperContent } from './styles'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'

type Props = LiveReactChartProps<
    WellOperationsNavigationState,
    WellOperationsNavigationMessage,
    WellOperationsNavigationConfig
>

export const WellOperationsNavigation = ({ widget }: Props): JSX.Element => {
    const mode = Utils.isMobile() ? 'phone' : 'desktop'
    const INITIAL_HEIGHT = 170
    const CUSTOM_HEIGHT = React.useRef(INITIAL_HEIGHT) //250 //170

    const dashboardContext = React.useContext(DashboardContext)
    const [breadCrumbs, setSelectedState] = React.useState<BreadCrumbState>({
        wellIndex: 0,
        intervention: undefined,
        section_phase: undefined,
        run: undefined,
        selected: {
            type: null,
            name: null,
            begin: null,
            end: null
        }
    })
    const { data } = useStaticData(dashboardContext)

    const wellsSorted = data.sort((a, b) => (a?.interventions[0]?.begin < b?.interventions[0]?.begin ? -1 : 1))

    useDashboardHeight(dashboardContext, mode, CUSTOM_HEIGHT.current, widget.id, window.location.href)
    useDashboardTimestamp(breadCrumbs, dashboardContext)

    function handleBlockSelectedClick(info: Info, indexHierarchy: number[]): void {
        const [infoChecked, hasZero] = checkIfTimestampIsZero(info)

        setSelectedState({
            wellIndex: info?.wellIndex,
            intervention: indexHierarchy[0] ?? undefined,
            section_phase: indexHierarchy[1] ?? undefined,
            run: indexHierarchy[2] ?? undefined,
            selected:
                breadCrumbs.selected.type === info.type && breadCrumbs.selected.name === info.name
                    ? { name: null, type: null, begin: null, end: null }
                    : { ...infoChecked }
        })

        if (info?.begin && info?.end && dashboardContext) {
            if (hasZero) {
                globalNotification.warn(
                    `Fields start and end is empty or incorrect - start: ${info?.begin} and end: ${info?.end}`
                )
            } else {
                setDashboardTimespan(dashboardContext, checkAndMountTimestamp(info?.begin, info?.end))
            }
        }
    }

    return (
        <Container>
            {wellsSorted.length > 0 ? (
                wellsSorted.map((_, wellIndex) => {
                    return (
                        <WrapperContent>
                            <InnerContainer>
                                <div className="menu-left-side">
                                    <span>{i18n('INTERVENTION')}</span>
                                    <span>{i18n('SECTION/PHASE')}</span>
                                    <span>{i18n('RUN')}</span>
                                </div>
                                {wellsSorted[wellIndex]?.interventions?.map((intervention, index) => {
                                    const hasSection = intervention.sections?.some(section => section !== null)
                                    const hasRun = intervention.sections?.some(s => s?.runs?.length > 0)

                                    return (
                                        <Item key={`__${index}`}>
                                            <ItemRow>
                                                <OverlayTrigger
                                                    placement="bottom"
                                                    overlay={
                                                        <Tooltip style={{ zIndex: 9999 }} id="tooltip-info">
                                                            <div>
                                                                <span
                                                                    style={{
                                                                        color: 'var(--gray-500)',
                                                                        fontWeight: 700
                                                                    }}
                                                                >
                                                                    {i18n('Intervention name')}:{' '}
                                                                    <strong
                                                                        style={{
                                                                            color: 'var(--gray-0)',
                                                                            fontWeight: 700
                                                                        }}
                                                                    >
                                                                        {intervention.name}
                                                                    </strong>
                                                                </span>
                                                            </div>
                                                        </Tooltip>
                                                    }
                                                >
                                                    <Block
                                                        className={selectedInterventionBlock(
                                                            breadCrumbs,
                                                            wellIndex,
                                                            index
                                                        )}
                                                        onClick={() =>
                                                            handleBlockSelectedClick(
                                                                mountInterventionInfo(intervention, wellIndex),
                                                                [index]
                                                            )
                                                        }
                                                        isStatusOperationFinished={!!intervention?.end}
                                                    />
                                                </OverlayTrigger>
                                            </ItemRow>
                                            <ItemRow>
                                                {hasSection ? (
                                                    intervention?.sections?.map((section, section_index) => {
                                                        return (
                                                            section && (
                                                                <OverlayTrigger
                                                                    placement="bottom"
                                                                    overlay={
                                                                        <Tooltip
                                                                            style={{ zIndex: 9999 }}
                                                                            id="tooltip-info"
                                                                        >
                                                                            <div>
                                                                                <span
                                                                                    style={{
                                                                                        color: 'var(--gray-500)',
                                                                                        fontWeight: 700
                                                                                    }}
                                                                                >
                                                                                    {i18n('Section OD')}:{' '}
                                                                                    <strong
                                                                                        style={{
                                                                                            color: 'var(--gray-0)',
                                                                                            fontWeight: 700
                                                                                        }}
                                                                                    >
                                                                                        {section?.od?.value}{' '}
                                                                                        {section?.od?.name}
                                                                                    </strong>
                                                                                </span>
                                                                            </div>
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <Block
                                                                        isPhase
                                                                        key={`section_index_${section_index}`}
                                                                        countRuns={section?.runs?.length}
                                                                        className={selectedSectionBlock(
                                                                            breadCrumbs,
                                                                            wellIndex,
                                                                            index,
                                                                            section_index
                                                                        )}
                                                                        onClick={() => {
                                                                            handleBlockSelectedClick(
                                                                                mountSectionInfo(section, wellIndex),
                                                                                [index, section_index]
                                                                            )
                                                                        }}
                                                                        isStatusOperationFinished={!!section?.end}
                                                                    />
                                                                </OverlayTrigger>
                                                            )
                                                        )
                                                    })
                                                ) : (
                                                    <OverlayTrigger
                                                        placement="bottom"
                                                        overlay={
                                                            <Tooltip style={{ zIndex: 9999 }} id="tooltip-info">
                                                                {i18n('Section not started')}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <Block className="no-data" isPhase noData />
                                                    </OverlayTrigger>
                                                )}
                                            </ItemRow>
                                            <ItemRow>
                                                {hasRun ? (
                                                    intervention?.sections?.map((section, section_index) => {
                                                        return section?.runs?.map((run, run_index) => {
                                                            const runTypeFormatted = run?.type
                                                                .split('_')
                                                                .map(
                                                                    (word: string) =>
                                                                        word.charAt(0) + word.slice(1).toLowerCase()
                                                                )
                                                                .slice(0)
                                                                .join(' ') as string

                                                            return (
                                                                run && (
                                                                    <OverlayTrigger
                                                                        placement="bottom"
                                                                        overlay={
                                                                            <Tooltip
                                                                                style={{ zIndex: 9999 }}
                                                                                id="tooltip-info"
                                                                            >
                                                                                <div
                                                                                    style={{
                                                                                        display: 'flex',
                                                                                        flexDirection: 'column'
                                                                                    }}
                                                                                >
                                                                                    <span
                                                                                        style={{
                                                                                            color: 'var(--gray-500)',
                                                                                            fontWeight: 700
                                                                                        }}
                                                                                    >
                                                                                        {i18n('Run Number')}:{' '}
                                                                                        <strong
                                                                                            style={{
                                                                                                color: 'var(--gray-0)',
                                                                                                fontWeight: 700
                                                                                            }}
                                                                                        >
                                                                                            {run?.number}
                                                                                        </strong>
                                                                                    </span>
                                                                                    <span
                                                                                        style={{
                                                                                            color: 'var(--gray-500)',
                                                                                            fontWeight: 700
                                                                                        }}
                                                                                    >
                                                                                        {i18n('Run Type')}:{' '}
                                                                                        <span
                                                                                            style={{
                                                                                                color: 'var(--gray-0)',
                                                                                                fontWeight: 700,
                                                                                                textTransform:
                                                                                                    'capitalize'
                                                                                            }}
                                                                                        >
                                                                                            {runTypeFormatted}
                                                                                        </span>
                                                                                    </span>
                                                                                </div>
                                                                            </Tooltip>
                                                                        }
                                                                    >
                                                                        <Block
                                                                            isRun
                                                                            key={`run_run_index_${run_index}`}
                                                                            countRuns={section?.runs?.length}
                                                                            className={selectedRunBlock(
                                                                                breadCrumbs,
                                                                                wellIndex,
                                                                                index,
                                                                                section_index,
                                                                                run_index
                                                                            )}
                                                                            onClick={() =>
                                                                                handleBlockSelectedClick(
                                                                                    mountRunInfo(run, wellIndex),
                                                                                    [index, section_index, run_index]
                                                                                )
                                                                            }
                                                                            isStatusOperationFinished={!!run?.end}
                                                                        />
                                                                    </OverlayTrigger>
                                                                )
                                                            )
                                                        })
                                                    })
                                                ) : (
                                                    <OverlayTrigger
                                                        placement="bottom"
                                                        overlay={
                                                            <Tooltip style={{ zIndex: 9999 }} id="tooltip-info">
                                                                {i18n('Run not started')}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <Block className="no-data" isRun noData />
                                                    </OverlayTrigger>
                                                )}
                                            </ItemRow>
                                        </Item>
                                    )
                                })}
                            </InnerContainer>
                        </WrapperContent>
                    )
                })
            ) : (
                <WrapperContent>
                    <h1>{i18n('No Static Data found in the selected period')}</h1>
                </WrapperContent>
            )}
        </Container>
    )
}

export default WellOperationsNavigation
